/**
 * Helligkeit am Sitz, nur in diesem Browser.
 * Gespeichert wird ausschließlich Hell oder Dunkel — kein Server, keine Secrets.
 * Standard ist Dunkel, bis der Anwalt umschaltet.
 */

export type PortalAppearance = 'dark' | 'light';

export const PORTAL_APPEARANCE_CHANGED = 'arkorbit:portal-appearance';

/** Marker nur an der Arbeitsoberfläche, wenn Hell gewählt ist. */
export const PORTAL_HOME_LIGHT_CLASS = 'portal-home-light';

/** Marker nur an der Hauptleiste, wenn Hell gewählt ist. Nicht an anderen Reiter-Inhalten. */
export const PORTAL_SHELL_LIGHT_CLASS = 'portal-shell-light';

/** Marker nur am ARKO-Inhalt, wenn Hell gewählt ist. */
export const PORTAL_ARKO_LIGHT_CLASS = 'portal-arko-light';

/** Marker nur am Suche-Inhalt, wenn Hell gewählt ist. */
export const PORTAL_SUCHE_LIGHT_CLASS = 'portal-suche-light';

/** Marker nur am Diktat-Inhalt, wenn Hell gewählt ist. */
export const PORTAL_DIKTAT_LIGHT_CLASS = 'portal-diktat-light';

/** Marker nur am Dokumente-Inhalt, wenn Hell gewählt ist. */
export const PORTAL_DOKUMENTE_LIGHT_CLASS = 'portal-dokumente-light';

/** Marker nur am Merkzettel-Fenster, wenn Hell gewählt ist. Nicht an Gedächtnis oder Brain. */
export const PORTAL_MERKZETTEL_LIGHT_CLASS = 'portal-merkzettel-light';

/** Marker nur an der Kanzlei-Chat-Hülle, wenn Hell gewählt ist. Nicht am Element-Fenster. */
export const PORTAL_KANZLEI_CHAT_LIGHT_CLASS = 'portal-kanzlei-chat-light';

/** Marker nur am Akten-Inhalt, wenn Hell gewählt ist. */
export const PORTAL_AKTEN_LIGHT_CLASS = 'portal-akten-light';

/** Marker nur am Kalender-Inhalt, wenn Hell gewählt ist. */
export const PORTAL_KALENDER_LIGHT_CLASS = 'portal-kalender-light';

/** Marker nur am Reiter Baustein unter Mehr, wenn Hell gewählt ist. */
export const PORTAL_BAUSTEIN_LIGHT_CLASS = 'portal-baustein-light';

/** Marker nur am Reiter PDF-Werkzeuge, wenn Hell gewählt ist. Nicht am Stirling-Fenster. */
export const PORTAL_PDF_LIGHT_CLASS = 'portal-pdf-light';

/** Marker nur am Reiter Vertrag erstellen unter Mehr, wenn Hell gewählt ist. Nicht in der Akten-Einbettung. */
export const PORTAL_VERTRAG_LIGHT_CLASS = 'portal-vertrag-light';

/** Marker nur am Reiter Fallanalyse unter Mehr, wenn Hell gewählt ist. */
export const PORTAL_FALLANALYSE_LIGHT_CLASS = 'portal-fallanalyse-light';

/** Marker nur am Reiter Kanzlei & Zugänge, wenn Hell gewählt ist. */
export const PORTAL_ZUGAENGE_LIGHT_CLASS = 'portal-zugaenge-light';

/** Marker nur am Brain-Reiter unter Mehr, wenn Hell gewählt ist. Nicht an Gedächtnis-Dialogen. */
export const PORTAL_BRAIN_LIGHT_CLASS = 'portal-brain-light';

/** Marker nur am Interview-Fenster, wenn Hell gewählt ist. Nicht an anderen Gedächtnis-Dialogen. */
export const PORTAL_ONBOARDING_LIGHT_CLASS = 'portal-onboarding-light';

/** Warmer Stein. Kein Weiß, kein kühles Blau-Grau. */
export const PORTAL_HOME_LIGHT = {
  ground: '#e6e1d8',
  raised: '#f3efe8',
  text: '#1c1915',
  muted: '#3f3832',
  accent: '#5b4678',
  edge: '#6a5684',
} as const;

type AppearanceStorage = Pick<Storage, 'getItem' | 'setItem'>;

function portalAppearanceStorageKey(seatId: string): string {
  const safe = String(seatId).replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 80);
  return `arkorbit.portal.appearance.v1.${safe}`;
}

export function loadPortalAppearance(
  storage: Pick<Storage, 'getItem'> | null,
  seatId: string,
): PortalAppearance {
  if (!storage || !seatId.trim()) return 'dark';
  const raw = storage.getItem(portalAppearanceStorageKey(seatId));
  return raw === 'light' ? 'light' : 'dark';
}

function dispatchAppearanceChange(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PORTAL_APPEARANCE_CHANGED));
}

/** Nur die Wahl. Kein Name, keine Kanzlei, kein weiteres Feld. */
export function savePortalAppearance(
  storage: AppearanceStorage | null,
  seatId: string,
  appearance: PortalAppearance,
): void {
  if (!storage || !seatId.trim()) return;
  const value: PortalAppearance = appearance === 'light' ? 'light' : 'dark';
  storage.setItem(portalAppearanceStorageKey(seatId), value);
  dispatchAppearanceChange();
}

/** Helle Klasse nur für die Arbeitsoberfläche. Dunkel und jede andere Ansicht: leer. */
export function portalHomeSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_HOME_LIGHT_CLASS : '';
}

/** Helle Klasse nur für die Hauptleiste. Andere Reiter-Inhalte bleiben ohne sie. */
export function portalShellSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_SHELL_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den ARKO-Inhalt. Dunkel und jeder andere Reiter: leer. */
export function portalArkoSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_ARKO_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den Suche-Inhalt. Dunkel und jeder andere Reiter: leer. */
export function portalSucheSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_SUCHE_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den Diktat-Inhalt. Dunkel und jeder andere Reiter: leer. */
export function portalDiktatSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_DIKTAT_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den Dokumente-Inhalt. Dunkel und jeder andere Reiter: leer. */
export function portalDokumenteSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_DOKUMENTE_LIGHT_CLASS : '';
}

/** Helle Klasse nur für das Merkzettel-Fenster. Dunkel und jeder andere Reiter: leer. */
export function portalMerkzettelSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_MERKZETTEL_LIGHT_CLASS : '';
}

/** Helle Klasse nur für die Kanzlei-Chat-Hülle. Dunkel: leer. Nicht für die Element-Fläche. */
export function portalKanzleiChatSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_KANZLEI_CHAT_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den Akten-Inhalt. Dunkel und jeder andere Reiter: leer. */
export function portalAktenSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_AKTEN_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den Kalender-Inhalt. Dunkel und jeder andere Reiter: leer. */
export function portalKalenderSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_KALENDER_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den Reiter Baustein unter Mehr. Dunkel und jeder andere Reiter: leer. */
export function portalBausteinSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_BAUSTEIN_LIGHT_CLASS : '';
}

/** Helle Klasse nur für PDF-Werkzeuge. Dunkel und jeder andere Reiter: leer. */
export function portalPdfSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_PDF_LIGHT_CLASS : '';
}

/** Helle Klasse nur für Vertrag erstellen unter Mehr. Dunkel und jeder andere Reiter: leer. */
export function portalVertragSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_VERTRAG_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den Reiter Fallanalyse unter Mehr. Dunkel und jeder andere Reiter: leer. */
export function portalFallanalyseSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_FALLANALYSE_LIGHT_CLASS : '';
}

/** Helle Klasse nur für Kanzlei & Zugänge. Dunkel und jeder andere Reiter: leer. */
export function portalZugaengeSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_ZUGAENGE_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den Brain-Reiter unter Mehr. Dunkel: leer. Nicht für Gedächtnis-Dialoge. */
export function portalBrainSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_BRAIN_LIGHT_CLASS : '';
}

/** Helle Klasse nur für das Onboarding-Interview. Dunkel: leer. Andere Dialoge bleiben dunkel. */
export function portalOnboardingSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_ONBOARDING_LIGHT_CLASS : '';
}

/** Helle Klasse nur für „Wirklich im Gedächtnis speichern?“. Dunkel: leer. */
export const PORTAL_MEMORY_THUMB_LIGHT_CLASS = 'portal-memory-thumb-light';

export function portalMemoryThumbSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_MEMORY_THUMB_LIGHT_CLASS : '';
}

/** Marker nur am Reiter Tasten, wenn Hell gewählt ist. */
export const PORTAL_TASTEN_LIGHT_CLASS = 'portal-tasten-light';

/** Marker nur am Einstellungen-Dialog, wenn Hell gewählt ist. */
export const PORTAL_SETTINGS_LIGHT_CLASS = 'portal-settings-light';

/** Marker nur an Beta-Feedback, wenn Hell gewählt ist. */
export const PORTAL_FEEDBACK_LIGHT_CLASS = 'portal-feedback-light';

/** Marker nur am Schnellzugriff der Schale, wenn Hell gewählt ist. */
export const PORTAL_COMMAND_LIGHT_CLASS = 'portal-command-light';

/** Helle Klasse nur für den Reiter Tasten. Dunkel: leer. */
export function portalTastenSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_TASTEN_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den Einstellungen-Dialog. Dunkel: leer. */
export function portalSettingsSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_SETTINGS_LIGHT_CLASS : '';
}

/** Helle Klasse nur für Beta-Feedback. Dunkel: leer. */
export function portalFeedbackSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_FEEDBACK_LIGHT_CLASS : '';
}

/** Helle Klasse nur für den Schnellzugriff. Dunkel: leer. */
export function portalCommandSurfaceClass(appearance: PortalAppearance): string {
  return appearance === 'light' ? PORTAL_COMMAND_LIGHT_CLASS : '';
}
